import {
    BodySmallRegular,
    H6Emphasized,
    LabelRegular,
    XStack,
    YStack,
} from 'smoothie';

import {
    rubricBandColor,
    rubricBracketLabel,
    tallyJudgeRubric,
    UNSCORED_BRACKET_KEY,
} from './rubricBracketTally';

const unscoredCount = (totalItems: number, scored: number): number =>
    Math.max(totalItems - scored, 0);

const filterCaption = (selectedBracket: string | null): string => {
    if (selectedBracket == null) {
        return 'Select a bracket to filter cases.';
    }
    if (selectedBracket === UNSCORED_BRACKET_KEY) {
        return 'Filtered to not scored.';
    }
    return `Filtered to ${selectedBracket}.`;
};

const RubricTile = ({
    count,
    label,
    summary,
    color,
    selected,
    onPress,
}: {
    count: number;
    label: string;
    summary: string;
    color: string;
    selected: boolean;
    onPress: () => void;
}) => (
    <YStack
        flex={1}
        minWidth={140}
        gap={8}
        padding={12}
        borderRadius={12}
        borderWidth={2}
        borderColor={selected ? color : 'transparent'}
        backgroundColor={selected ? 'white' : '$gray100'}
        cursor="pointer"
        hoverStyle={{ backgroundColor: selected ? 'white' : '$gray200' }}
        onPress={onPress}
    >
        <YStack height={4} borderRadius={2} backgroundColor={color} />
        <H6Emphasized color={count === 0 ? '$gray1000' : color}>
            {String(count)}
        </H6Emphasized>
        <YStack gap={2}>
            <LabelRegular>{label}</LabelRegular>
            <BodySmallRegular color="$gray1000">{summary}</BodySmallRegular>
        </YStack>
    </YStack>
);

export const RubricSummary = ({
    scores,
    totalItems,
    selectedBracket,
    onSelectBracket,
}: {
    scores: readonly number[];
    totalItems: number;
    selectedBracket: string | null;
    onSelectBracket: (bracket: string | null) => void;
}) => {
    const brackets = tallyJudgeRubric(scores);
    const select = (bracket: string) => {
        onSelectBracket(selectedBracket === bracket ? null : bracket);
    };

    return (
        <YStack
            backgroundColor="white"
            borderColor="$gray200"
            borderWidth={1}
            borderRadius={20}
            padding={20}
            gap={16}
        >
            <XStack justifyContent="space-between" alignItems="center" gap={12}>
                <YStack flex={1} gap={2}>
                    <H6Emphasized>Judge rubric</H6Emphasized>
                    <BodySmallRegular color="$gray1000">
                        {filterCaption(selectedBracket)}
                    </BodySmallRegular>
                </YStack>
                {selectedBracket != null ? (
                    <XStack
                        cursor="pointer"
                        padding={4}
                        onPress={() => {
                            onSelectBracket(null);
                        }}
                    >
                        <BodySmallRegular>Clear</BodySmallRegular>
                    </XStack>
                ) : null}
            </XStack>
            <XStack gap={8} flexWrap="wrap" alignItems="stretch">
                {brackets.map((band) => {
                    const bracket = rubricBracketLabel(band);
                    return (
                        <RubricTile
                            key={bracket}
                            count={band.count}
                            label={bracket}
                            summary={band.summary}
                            color={rubricBandColor(band.minPercent)}
                            selected={selectedBracket === bracket}
                            onPress={() => {
                                select(bracket);
                            }}
                        />
                    );
                })}
                <RubricTile
                    count={unscoredCount(totalItems, scores.length)}
                    label="Not scored"
                    summary="No judge score"
                    color="$gray1000"
                    selected={selectedBracket === UNSCORED_BRACKET_KEY}
                    onPress={() => {
                        select(UNSCORED_BRACKET_KEY);
                    }}
                />
            </XStack>
        </YStack>
    );
};
